import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Upload, X, Plus, Trash2, GripVertical, UserPlus, Users, Shield, LogOut, Download, Database, AlertTriangle, Percent } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, accessToken, isOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Business Information
  const [businessInfo, setBusinessInfo] = useState({
    business_name: "",
    business_address: "",
    business_phone: "",
    business_email: "",
    business_logo: null,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Tax Settings
  const [taxSettings, setTaxSettings] = useState({
    enabled: false,
    tax_name: "VAT",
    tax_rate: 0,
    tax_id: "",
    include_in_price: false
  });
  
  // Payment Methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [newPayment, setNewPayment] = useState({ name: "", details: "" });
  const [paymentTerms, setPaymentTerms] = useState("");
  
  // Workflows
  const [sublimationWorkflow, setSublimationWorkflow] = useState([]);
  const [embroideryWorkflow, setEmbroideryWorkflow] = useState([]);
  const [newSublimationStep, setNewSublimationStep] = useState("");
  const [newEmbroideryStep, setNewEmbroideryStep] = useState("");
  
  // Staff Management
  const [staffList, setStaffList] = useState([]);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [newStaff, setNewStaff] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "staff"
  });
  const [editingStaff, setEditingStaff] = useState(null);
  
  // Data Export/Import
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = {
    headers: { Authorization: `Bearer ${accessToken}` }
  };

  useEffect(() => {
    fetchSettings();
    if (isOwner) {
      fetchStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const settings = response.data;
      
      setBusinessInfo({
        business_name: settings.business_name || "",
        business_address: settings.business_address || "",
        business_phone: settings.business_phone || "",
        business_email: settings.business_email || "",
        business_logo: settings.business_logo || null,
      });
      
      if (settings.business_logo) {
        setLogoPreview(settings.business_logo);
      }
      
      // Load tax settings
      if (settings.tax_settings) {
        setTaxSettings({
          enabled: settings.tax_settings.enabled || false,
          tax_name: settings.tax_settings.tax_name || "VAT",
          tax_rate: settings.tax_settings.tax_rate || 0,
          tax_id: settings.tax_settings.tax_id || "",
          include_in_price: settings.tax_settings.include_in_price || false
        });
      }
      
      setPaymentMethods(settings.payment_methods || []);
      setPaymentTerms(settings.payment_terms || "");
      setSublimationWorkflow(settings.sublimation_workflow || []);
      setEmbroideryWorkflow(settings.embroidery_workflow || []);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/users`, authHeaders);
      setStaffList(response.data);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const handleAddStaff = async () => {
    if (!newStaff.email || !newStaff.password || !newStaff.full_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      await axios.post(`${API}/auth/register`, newStaff, authHeaders);
      toast.success("Staff member added successfully");
      setNewStaff({ email: "", password: "", full_name: "", role: "staff" });
      setShowAddStaff(false);
      fetchStaff();
    } catch (error) {
      console.error("Error adding staff:", error);
      toast.error(error.response?.data?.detail || "Failed to add staff member");
    }
  };

  const handleUpdateStaff = async (userId, updateData) => {
    try {
      await axios.put(`${API}/users/${userId}`, updateData, authHeaders);
      toast.success("Staff member updated");
      fetchStaff();
      setEditingStaff(null);
    } catch (error) {
      console.error("Error updating staff:", error);
      toast.error("Failed to update staff member");
    }
  };

  const handleDeleteStaff = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${userId}`, authHeaders);
      toast.success("Staff member deleted");
      fetchStaff();
    } catch (error) {
      console.error("Error deleting staff:", error);
      toast.error(error.response?.data?.detail || "Failed to delete staff member");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setBusinessInfo((prev) => ({ ...prev, business_logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setBusinessInfo((prev) => ({ ...prev, business_logo: null }));
  };

  const addPaymentMethod = () => {
    if (!newPayment.name || !newPayment.details) {
      toast.error("Please fill in payment method details");
      return;
    }
    setPaymentMethods([...paymentMethods, newPayment]);
    setNewPayment({ name: "", details: "" });
  };

  const removePaymentMethod = (index) => {
    setPaymentMethods(paymentMethods.filter((_, i) => i !== index));
  };

  const addSublimationStep = () => {
    if (!newSublimationStep.trim()) {
      toast.error("Please enter a step name");
      return;
    }
    setSublimationWorkflow([...sublimationWorkflow, newSublimationStep.trim()]);
    setNewSublimationStep("");
  };

  const removeSublimationStep = (index) => {
    setSublimationWorkflow(sublimationWorkflow.filter((_, i) => i !== index));
  };

  const addEmbroideryStep = () => {
    if (!newEmbroideryStep.trim()) {
      toast.error("Please enter a step name");
      return;
    }
    setEmbroideryWorkflow([...embroideryWorkflow, newEmbroideryStep.trim()]);
    setNewEmbroideryStep("");
  };

  const removeEmbroideryStep = (index) => {
    setEmbroideryWorkflow(embroideryWorkflow.filter((_, i) => i !== index));
  };

  const handleDragEndSublimation = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(sublimationWorkflow);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setSublimationWorkflow(items);
  };

  const handleDragEndEmbroidery = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(embroideryWorkflow);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setEmbroideryWorkflow(items);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        ...businessInfo,
        tax_settings: taxSettings,
        payment_methods: paymentMethods,
        payment_terms: paymentTerms,
        sublimation_workflow: sublimationWorkflow,
        embroidery_workflow: embroideryWorkflow,
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  // Export data function
  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/data/export`, authHeaders);
      const data = response.data;
      
      // Create and download JSON file
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `printdash-backup-${new Date().toISOString().split('T')[0]}.json`);
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Exported ${data.orders?.length || 0} orders successfully!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data: " + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  // Import data function
  const handleImportData = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/data/import`, formData, {
        headers: {
          ...authHeaders.headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResults(response.data);
      
      if (response.data.orders_imported > 0) {
        toast.success(`Imported ${response.data.orders_imported} orders!`);
      } else if (response.data.orders_skipped > 0) {
        toast.info(`${response.data.orders_skipped} orders already exist`);
      }
      
      if (response.data.settings_updated) {
        toast.success("Settings updated from backup");
        fetchSettings(); // Refresh settings
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import data");
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-heading font-extrabold tracking-tight text-primary">
                  Settings
                </h1>
                <p className="text-sm text-muted-foreground">Manage your business settings</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right mr-4 hidden md:block">
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary"
                data-testid="save-settings-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-5xl">
        <Tabs defaultValue="payment" className="space-y-6">
          <TabsList className={`grid w-full ${isOwner ? 'grid-cols-4' : 'grid-cols-2'}`}>
            <TabsTrigger value="payment" data-testid="tab-payment">Payment Methods</TabsTrigger>
            <TabsTrigger value="workflow" data-testid="tab-workflow">Workflows</TabsTrigger>
            {isOwner && (
              <TabsTrigger value="staff" data-testid="tab-staff">
                <Users className="h-4 w-4 mr-2" />
                Staff
              </TabsTrigger>
            )}
            {isOwner && (
              <TabsTrigger value="data" data-testid="tab-data">
                <Database className="h-4 w-4 mr-2" />
                Data
              </TabsTrigger>
            )}
          </TabsList>

          {/* Payment Methods Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Methods</CardTitle>
                <CardDescription>
                  Add payment methods to display on invoices
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Payment Method */}
                <div className="p-4 bg-secondary rounded-md space-y-3">
                  <h3 className="font-semibold text-sm">Add Payment Method</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      placeholder="Method name (e.g., Bank Transfer)"
                      value={newPayment.name}
                      onChange={(e) => setNewPayment((prev) => ({ ...prev, name: e.target.value }))}
                      data-testid="input-payment-name"
                    />
                    <Input
                      placeholder="Details (e.g., Account: 123456789)"
                      value={newPayment.details}
                      onChange={(e) => setNewPayment((prev) => ({ ...prev, details: e.target.value }))}
                      data-testid="input-payment-details"
                    />
                  </div>
                  <Button onClick={addPaymentMethod} size="sm" data-testid="add-payment-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Payment Method
                  </Button>
                </div>

                {/* Payment Methods List */}
                <div className="space-y-3">
                  {paymentMethods.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No payment methods added yet
                    </p>
                  ) : (
                    paymentMethods.map((method, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border border-border rounded-md"
                        data-testid={`payment-method-${index}`}
                      >
                        <div>
                          <p className="font-medium">{method.name}</p>
                          <p className="text-sm text-muted-foreground">{method.details}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removePaymentMethod(index)}
                          className="text-red-600"
                          data-testid={`remove-payment-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {/* Payment Terms */}
                <div className="mt-6">
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Textarea
                    id="payment_terms"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    placeholder="Payment is due within 30 days of invoice date..."
                    rows={3}
                    data-testid="input-payment-terms"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This description will appear on invoices
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workflows Tab */}
          <TabsContent value="workflow" className="space-y-6">
            {/* Sublimation Workflow */}
            <Card>
              <CardHeader>
                <CardTitle>Sublimation Workflow</CardTitle>
                <CardDescription>
                  Customize the production steps for sublimation orders. Drag to reorder.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Step */}
                <div className="flex gap-2">
                  <Input
                    placeholder="New step name"
                    value={newSublimationStep}
                    onChange={(e) => setNewSublimationStep(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addSublimationStep()}
                    data-testid="input-sublimation-step"
                  />
                  <Button onClick={addSublimationStep} data-testid="add-sublimation-step">
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>

                {/* Workflow Steps */}
                <DragDropContext onDragEnd={handleDragEndSublimation}>
                  <Droppable droppableId="sublimation-workflow">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-2"
                      >
                        {sublimationWorkflow.map((step, index) => (
                          <Draggable key={`sub-${index}`} draggableId={`sub-${index}`} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="flex items-center gap-2 p-3 bg-secondary border border-border rounded-md"
                                data-testid={`sublimation-step-${index}`}
                              >
                                <div {...provided.dragHandleProps}>
                                  <GripVertical className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <span className="flex-1 font-medium">
                                  {index + 1}. {step}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeSublimationStep(index)}
                                  className="text-red-600"
                                  data-testid={`remove-sublimation-${index}`}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </CardContent>
            </Card>

            {/* Embroidery Workflow */}
            <Card>
              <CardHeader>
                <CardTitle>Embroidery Workflow</CardTitle>
                <CardDescription>
                  Customize the production steps for embroidery orders. Drag to reorder.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Step */}
                <div className="flex gap-2">
                  <Input
                    placeholder="New step name"
                    value={newEmbroideryStep}
                    onChange={(e) => setNewEmbroideryStep(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addEmbroideryStep()}
                    data-testid="input-embroidery-step"
                  />
                  <Button onClick={addEmbroideryStep} data-testid="add-embroidery-step">
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>

                {/* Workflow Steps */}
                <DragDropContext onDragEnd={handleDragEndEmbroidery}>
                  <Droppable droppableId="embroidery-workflow">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-2"
                      >
                        {embroideryWorkflow.map((step, index) => (
                          <Draggable key={`emb-${index}`} draggableId={`emb-${index}`} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="flex items-center gap-2 p-3 bg-secondary border border-border rounded-md"
                                data-testid={`embroidery-step-${index}`}
                              >
                                <div {...provided.dragHandleProps}>
                                  <GripVertical className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <span className="flex-1 font-medium">
                                  {index + 1}. {step}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeEmbroideryStep(index)}
                                  className="text-red-600"
                                  data-testid={`remove-embroidery-${index}`}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Accounts Tab - Admin Only */}
          {isOwner && (
            <TabsContent value="staff" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Staff Accounts</CardTitle>
                      <CardDescription>
                        Manage staff members who can access the system
                      </CardDescription>
                    </div>
                    <Button onClick={() => setShowAddStaff(true)} data-testid="add-staff-button">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Staff
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Staff Form */}
                  {showAddStaff && (
                    <div className="p-4 bg-secondary rounded-lg space-y-4 mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <UserPlus className="h-4 w-4" />
                        Add New Staff Member
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="staff-name">Full Name *</Label>
                          <Input
                            id="staff-name"
                            placeholder="John Doe"
                            value={newStaff.full_name}
                            onChange={(e) => setNewStaff(prev => ({ ...prev, full_name: e.target.value }))}
                            data-testid="input-staff-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="staff-email">Email *</Label>
                          <Input
                            id="staff-email"
                            type="email"
                            placeholder="staff@company.com"
                            value={newStaff.email}
                            onChange={(e) => setNewStaff(prev => ({ ...prev, email: e.target.value }))}
                            data-testid="input-staff-email"
                          />
                        </div>
                        <div>
                          <Label htmlFor="staff-password">Password *</Label>
                          <Input
                            id="staff-password"
                            type="password"
                            placeholder="Enter password"
                            value={newStaff.password}
                            onChange={(e) => setNewStaff(prev => ({ ...prev, password: e.target.value }))}
                            data-testid="input-staff-password"
                          />
                        </div>
                        <div>
                          <Label htmlFor="staff-role">Role</Label>
                          <Select
                            value={newStaff.role}
                            onValueChange={(value) => setNewStaff(prev => ({ ...prev, role: value }))}
                          >
                            <SelectTrigger data-testid="select-staff-role">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">Staff</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="owner">Owner</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleAddStaff} data-testid="confirm-add-staff">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Staff Member
                        </Button>
                        <Button variant="outline" onClick={() => setShowAddStaff(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Staff List */}
                  <div className="space-y-3">
                    {staffList.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No staff members found
                      </p>
                    ) : (
                      staffList.map((staff) => (
                        <div
                          key={staff.id}
                          className="flex items-center justify-between p-4 border border-border rounded-lg"
                          data-testid={`staff-item-${staff.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              staff.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                              staff.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {staff.role === 'owner' ? (
                                <Shield className="h-5 w-5" />
                              ) : staff.role === 'admin' ? (
                                <Shield className="h-5 w-5" />
                              ) : (
                                <Users className="h-5 w-5" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{staff.full_name}</p>
                              <p className="text-sm text-muted-foreground">{staff.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              staff.role === 'owner'
                                ? 'bg-purple-100 text-purple-700'
                                : staff.role === 'admin' 
                                ? 'bg-amber-100 text-amber-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {staff.role}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              staff.is_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {staff.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {staff.id !== user?.id && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUpdateStaff(staff.id, { is_active: !staff.is_active })}
                                  data-testid={`toggle-staff-${staff.id}`}
                                >
                                  {staff.is_active ? 'Deactivate' : 'Activate'}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleDeleteStaff(staff.id)}
                                  className="text-red-600"
                                  data-testid={`delete-staff-${staff.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Data Management Tab (Admin Only) */}
          {isOwner && (
            <TabsContent value="data" className="space-y-6">
              {/* Export Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="h-5 w-5" />
                    Export Data
                  </CardTitle>
                  <CardDescription>
                    Download all your business data as a JSON backup file
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This will export all orders, settings, and user accounts (excluding passwords) to a JSON file 
                    that you can use to restore your data later or transfer to a new deployment.
                  </p>
                  <Button
                    onClick={handleExportData}
                    disabled={exporting}
                    className="bg-primary"
                    data-testid="export-data-button"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exporting ? "Exporting..." : "Export All Data"}
                  </Button>
                </CardContent>
              </Card>

              {/* Import Data */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Import Data
                  </CardTitle>
                  <CardDescription>
                    Restore data from a previously exported JSON backup file
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-semibold">Important Notes:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>Existing orders with the same ID will be skipped</li>
                          <li>Settings will be overwritten with the imported values</li>
                          <li>User accounts are NOT imported for security reasons</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".json"
                      onChange={handleImportData}
                      className="hidden"
                      data-testid="import-file-input"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={importing}
                      variant="outline"
                      data-testid="import-data-button"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {importing ? "Importing..." : "Select Backup File"}
                    </Button>
                  </div>

                  {/* Import Results */}
                  {importResults && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">Import Complete</h4>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>Orders imported: {importResults.orders_imported}</li>
                        <li>Orders skipped (already exist): {importResults.orders_skipped}</li>
                        <li>Settings updated: {importResults.settings_updated ? "Yes" : "No"}</li>
                        {importResults.errors?.length > 0 && (
                          <li className="text-red-600">
                            Errors: {importResults.errors.join(", ")}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Data Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Backup Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Export your data regularly (weekly or after major updates)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Store backup files in a safe location (cloud storage, external drive)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Test your backups occasionally by importing to a fresh deployment
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Keep multiple backup versions in case of data corruption
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
